<template>
  <v-container fluid>
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2>{{ $t('fleet_health' )}}</h2>
        <info-modal>
          <template v-slot:button="{ on, attrs }">
            <v-chip class="ml-4" dark v-bind="attrs" v-on="on">
              <v-icon class="mr-1">mdi-information-outline</v-icon>
              {{ $t('beta') }}
            </v-chip>
          </template>
          <v-card-title>
            <h4>{{ $t('fleet_health_ui.beta_modal_title') }}</h4>
          </v-card-title>
          <v-card-text v-html="$t('fleet_health_ui.beta_message')"/>
        </info-modal>
      </div>
      <vf-tooltip v-if="customers.length > 1" :text="$t('toggle_all_customers')" left>
        <v-switch v-model="showAllCustomers" :label="customerLabel"/>
      </vf-tooltip>
    </div>
    <v-row>
      <v-col lg="4" md="6" cols="12">
        <fleet-health-overview :fleet-health-data="fleetHealthData" :loading="loading" class="dashboard-card"/>
      </v-col>
      <v-col lg="4" md="6" cols="12">
        <vehicle-issue-summary :fleet-health-data="fleetHealthData" :loading="loading" class="dashboard-card" />
      </v-col>
      <v-col lg="4" cols="12">
        <temperature-violations :show-all-customers="showAllCustomers" class="dashboard-card" />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <unhealthy-vehicles-table :show-all-customers="showAllCustomers" :fleet-health-data="fleetHealthData" :loading="loading" v-on:vehicleFixed="refreshContents()"/>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="6" cols="12">
        <vehicles-excluded-from-dashboard :show-all-customers="showAllCustomers" class="dashboard-card-short" />
      </v-col>
      <v-col md="6" cols="12">
        <vehicles-marked-as-fixed ref="marked-as-fixed" :show-all-customers="showAllCustomers" class="dashboard-card-short"/>
      </v-col>
    </v-row>
  </v-container>

</template>

<script>
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import FleetHealthOverview from '@/components/fleet-health/FleetHealthOverview.vue'
import VehicleIssueSummary from '@/components/fleet-health/VehicleIssueSummary.vue'
import TemperatureViolations from '@/components/fleet-health/TemperatureViolations.vue'
import UnhealthyVehiclesTable from '@/components/fleet-health/UnhealthyVehiclesTable.vue'
import VehiclesExcludedFromDashboard from '@/components/fleet-health/VehiclesExcludedFromDashboard.vue'
import VehiclesMarkedAsFixed from '@/components/fleet-health/VehiclesMarkedAsFixed.vue'
import VfTooltip from '@/components/shared/VfTooltip'
import InfoModal from '@/components/shared/InfoModal'
import TemperatureMixin from '@/components/mixins/TemperatureMixin.js'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin.js'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import { map } from 'lodash'

export default {
  name: 'FleetHealth',
  components: {
    FleetHealthOverview,
    VehicleIssueSummary,
    TemperatureViolations,
    UnhealthyVehiclesTable,
    VehiclesExcludedFromDashboard,
    VehiclesMarkedAsFixed,
    VfTooltip,
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    TemperatureMixin,
    BrowserUtilitiesMixin
  ],
  data () {
    return {
      showAllCustomers: false,
      fleetHealthData: {},
      loading: true,
      nominalCoolerTemperature: RouteViolationRecord.DEFAULT_NOMINAL_COOLER_TEMPERATURE,
      nominalFreezerTemperature: RouteViolationRecord.DEFAULT_NOMINAL_FREEZER_TEMPERATURE
    }
  },
  methods: {

    /**
     * Retrieve the data from the API
     * @returns {Promise<void>}
     */
    fetchData () {
      this.loading = true
      try {
        const customerIds = this.showAllCustomers ? (map(this.regularCustomers, x => x.id)).join(',') : this.customerContext.id
        this.$httpAdapter.get('/customer-insights/fleet-health', {
          params: {
            customer_id: customerIds
          }
        }).then(resp => {
          this.fleetHealthData = resp.data
          this.loading = false
        })
      } catch (error) {
        this.loading = false
        this.handleError(error)
      }
    },

    /**
     * Causes all components to refresh their data from the API
     *
     * @return undefined
     */
    refreshContents () {
      this.fetchData()
      this.$refs['marked-as-fixed'].fetchData()
    },

    /**
     * Refresh the page when the customer context changes
     */
    customerContextChangeHandler () {
      this.fetchData()
    }
  },
  computed: {
    customerLabel () {
      if (this.showAllCustomers) {
        return this.$t('all_distribution_centers')
      } else {
        return this.customerContext.name
      }
    }
  },
  watch: {
    showAllCustomers () {
      this.fetchData()
    }
  },
  mounted () {
    this.fetchData()
    this.updateBrowserTitle(this.$t('fleet_health'))
  }
}
</script>

<style scoped lang="scss">
.dashboard-card {
  min-height: 365px;
}
.dashboard-card-short {
  min-height: 180px;
}
</style>
